import { Fragment, useCallback, useEffect, useState } from "react";
import { TableSortLabel, Tooltip, Typography } from "@mui/material";
import { Box, Stack } from "@mui/system";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  IconButton,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CreateOutlinedIcon from "@mui/icons-material/CreateOutlined";
import { useSelector } from "react-redux";
import {
  setSelectedSingleScopeID,
  setSortForScopesData,
} from "../../../../../../../../features/scopesTabSlice";
import { useDispatch } from "react-redux";
import EditScopeModal from "./EditScopeModal";
import { CheckPermissions } from "../../../../../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { selectUserData } from "../../../../../../../../features/userSlice";

// headers for the table columns with sortable option for each column
const tableheaders = [
  { id: 1, KEY: "name", label: "Scope", sortable: true },
  {
    id: 2,
    KEY: "campaign_name", // Use the nested key
    label: "Campaigns",
    sortable: true,
  },
];

const ManageTables = () => {
  const dispatch = useDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  const [openScope, setOpenScope] = useState<number | null>(null);
  const [sortedData, setSortedData] = useState<any[]>([]);
  const {
    scopesOfSTOperation,
    sortForScopesData,
    newScopeLoading,
    editScopeLoading,
    campaignToScopeLoading,
  } = useSelector((state: any) => state.searchTermOperation);
  const response = useSelector(selectUserData);
  const hasEditScopePermission = CheckPermissions(
    response?.data?.products?.paid_traffic,
    "plan_campaign_ppc",
    "create_st_account_management" // same for both(create and edit)
  );

  const toggleCollapse = (index: number) => {
    setOpenScope(openScope === index ? null : index);
  };

  const handleOpen = () => setOpenDialog(true);
  const handleClose = () => setOpenDialog(false);
  const handleEditScope = (row: any) => {
    dispatch(setSelectedSingleScopeID(row?.scope_shared_uid || ""));
    handleOpen();
  };

  const allowedStatuses = ["active", "enabled", "limited"]; //accepted statuses according to current requirement
  const filteredData = Array.isArray(sortedData)
    ? sortedData.map((item) => ({
        name: item?.name ?? "",
        scope_shared_uid: item?.shared_uid ?? null,
        campaigns: Array.isArray(item?.campaigns)
          ? item.campaigns
              .filter(
                (c: any) =>
                  allowedStatuses.includes(
                    String(c?.campaign_is_active || "").toLowerCase()
                  ) && c.mapping_is_active === true
              ) // Filter campaigns based on allowed statuses
              .map((c: any) => ({
                campaign_name: c?.campaign_name ?? "",
                campaign_shared_uid: c?.campaign_shared_uid ?? null,
              }))
          : [], // fallback if campaigns is missing or not an array
      }))
    : [];

  //-- Sorting Data
  const getSortedData = useCallback(
    (arrayToSort: any[]) => {
      if (!sortForScopesData.keytoSort || !sortForScopesData.direction) {
        // Default sorting by modified_at (To make new items appear first in your table when they are Created or Added)
        return arrayToSort;
      }

      return arrayToSort.slice().sort((a, b) => {
        const keytoSort = sortForScopesData.keytoSort;

        if (keytoSort === undefined) {
          return 0;
        }

        let aValue, bValue;

        if (keytoSort === "campaign_name") {
          aValue = a.campaigns?.[0]?.campaign_name ?? "";
          bValue = b.campaigns?.[0]?.campaign_name ?? "";
        } else {
          aValue = a[keytoSort];
          bValue = b[keytoSort];
        }

        if (aValue === undefined) return 1;
        if (bValue === undefined) return -1;

        if (aValue === "") return 1;
        if (bValue === "") return -1;

        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortForScopesData.direction === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        } else if (typeof aValue === "number" && typeof bValue === "number") {
          return sortForScopesData.direction === "asc"
            ? aValue - bValue
            : bValue - aValue;
        } else {
          return sortForScopesData.direction === "asc"
            ? String(aValue).localeCompare(String(bValue))
            : String(bValue).localeCompare(String(aValue));
        }
      });
    },
    [sortForScopesData]
  );

  //-- Function to handle click on table headers for sorting
  const handleHeaderClick = (header: any) => {
    if (!header.sortable) return; // Ignore click if column is not sortable

    dispatch(
      setSortForScopesData({
        keytoSort: header.KEY,
        // Toggle sort direction if the same column is clicked, otherwise default to ascending
        direction:
          header.KEY === sortForScopesData.keytoSort &&
          sortForScopesData.direction === "asc"
            ? "desc"
            : "asc",
      })
    );
  };

  //-- Set Sorted Data
  useEffect(() => {
    const sortedRows = getSortedData(scopesOfSTOperation || []);
    setSortedData(sortedRows);
    //eslint-disable-next-line
  }, [scopesOfSTOperation, getSortedData]);

  return (
    <Box p={2}>
      <TableContainer component={Paper} elevation={0} sx={{ mt: 1 }}>
        <Table aria-label="collapsible table" size="small">
          <TableHead>
            <TableRow>
              {tableheaders.map((header: any) => (
                <TableCell key={header.id} align="left">
                  <TableSortLabel
                    active={sortForScopesData.keytoSort === header.KEY}
                    direction={
                      sortForScopesData.keytoSort === header.KEY
                        ? sortForScopesData.direction
                        : "asc"
                    }
                    onClick={() => handleHeaderClick(header)}
                    disabled={!header.sortable}
                  >
                    {header.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData?.length > 0 ? (
              filteredData.map((row, index) => {
                const showLimit = 4; // show only first 4 campaigns
                const isOpen = openScope === index;

                return (
                  <Fragment key={row.scope_shared_uid || index}>
                    <TableRow
                      key={index}
                      sx={{
                        "& > *": {
                          borderBottom:
                            "1px solid rgba(224, 224, 224, 1) !important",
                        },
                      }}
                    >
                      {/* Scope Column with Edit Icon */}
                      <TableCell sx={{ paddingLeft: 0, width: "20%" }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {hasEditScopePermission ? (
                            <Tooltip title="Edit Scope" placement="top-end">
                              <IconButton
                                data-tracker="499"
                                size="small"
                                color="secondary"
                                onClick={() => handleEditScope(row)}
                                disabled={
                                  newScopeLoading ||
                                  editScopeLoading ||
                                  campaignToScopeLoading
                                }
                              >
                                <CreateOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <IconButton
                              data-tracker="499"
                              size="small"
                              disabled
                            >
                              <CreateOutlinedIcon fontSize="small" />
                            </IconButton>
                          )}

                          <Typography>{row?.name}</Typography>
                        </Stack>
                      </TableCell>

                      {/* Campaigns Column */}
                      <TableCell>
                        <Stack
                          direction="row"
                          flexWrap="wrap"
                          justifyContent={"space-between"}
                          alignItems="center"
                        >
                          <Stack direction="row" flexWrap="wrap" gap={1}>
                            {row.campaigns
                              .slice(0, showLimit)
                              .map((c: any, i: any) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  sx={{
                                    borderRadius: "12px",
                                    px: 1,
                                    py: 0.5,
                                    fontSize: "0.85rem",
                                    backgroundColor: "rgba(156, 39, 176, 0.04)",
                                    color: "text.primary",
                                  }}
                                >
                                  {c.campaign_name}
                                </Typography>
                              ))}
                          </Stack>
                          {row.campaigns.length > showLimit && (
                            <Stack>
                              <Link
                                href="#"
                                underline="none"
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleCollapse(index);
                                }}
                                sx={{
                                  fontSize: "0.85rem",
                                  color: "purple",
                                  ml: 1,
                                  textDecoration: "underline",
                                }}
                              >
                                {isOpen ? (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    Show less{" "}
                                    <ExpandLessIcon fontSize="small" />
                                  </Stack>
                                ) : (
                                  <Stack direction="row" alignItems="center">
                                    And {row.campaigns.length - showLimit} more,
                                    show all <ExpandMoreIcon fontSize="small" />
                                  </Stack>
                                )}
                              </Link>
                            </Stack>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>

                    {/* Collapse Row for hidden campaigns */}
                    {/* Collapse Row */}
                    <TableRow sx={{ display: isOpen ? "table-row" : "none" }}>
                      <TableCell />
                      <TableCell sx={{ paddingTop: 0, paddingBottom: 2 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ mt: 1, mb: 1, ml: 0.5, fontWeight: 500 }}
                        >
                          All Campaigns in Scope:
                        </Typography>
                        <Stack direction="row" flexWrap="wrap" gap={1}>
                          {row.campaigns?.length > 0 &&
                            row.campaigns
                              // .slice(showLimit)
                              .map((c: any, i: any) => (
                                <Typography
                                  key={i}
                                  variant="body2"
                                  sx={{
                                    borderRadius: "12px",
                                    px: 1,
                                    py: 0.5,
                                    fontSize: "0.85rem",
                                    backgroundColor: "rgba(156, 39, 176, 0.04)",
                                    color: "text.primary",
                                  }}
                                >
                                  {c.campaign_name}
                                </Typography>
                              ))}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body1" color="textSecondary">
                    No data available
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <EditScopeModal open={openDialog} onClose={handleClose} />
    </Box>
  );
};

export default ManageTables;
